import { ExtendedRecording } from '../recordings/state/recording.model';
import { IUser } from '../user/user.model';
import { Observable } from 'rxjs';

export enum EAssetManagementView {
  IN_PROGRESS_UPLOADS = 'IN_PROGRESS_UPLOADS',
  EDIT_FROM_CATALOG = 'EDIT_FROM_CATALOG',
  PRIVATE_UPLOADS = 'PRIVATE_UPLOADS',
}

export type TAssetManagementViewData = {
  view: EAssetManagementView;
};

interface PrivateAssetsPage {
  total: number;
  offset: number;
  pageSize: number;
}

export interface SelectSongEvent {
  event: MouseEvent;
  recording: ExtendedRecording;
}

export interface RecordingMetadata {
  id?: number;
  albumArtLink: string;
  albumTitle: string;
  duration?: number;
  artist: string;
  controlPercent: number;
  copyrightYear: number;
  label: string;
  lyrics: string;
  notes: string;
  releaseYear: number;
  title: string;
  watermarked: boolean;
  wcmControl: boolean;
  wmgMaster: boolean;
  writer: string;
}

export interface UploadAssetResponse {
  id?: number;
  recordingMetadata: RecordingMetadata;
  temporaryName: string;
}

export interface SaveAssetSubmit {
  data: UploadAssetResponse;
  coverArt?: File;
}

export interface SaveAssetResponse {
  albumArtLink?: string;
  albumTitle: string;
  artists: string[];
  bpm: number;
  countryControlPercent: number;
  duration: number;
  genres: string[];
  hasHiResolution: boolean;
  hasLyrics: boolean;
  hasTrackLink: boolean;
  id: number;
  themes: string[];
  type: 'RECORDING' | 'WORK';
  versions: string[];
  workAttached: boolean;
  writers: string[];
}

export enum UploadStatus {
  IDLE,
  UPLOAD_STARTED,
  UPLOADING,
  EDIT_LOADING,
  EDIT_STARTED,
  SAVING,
  ERROR_NO_FILE,
  ERROR_NO_AUDIO_FILE,
}

export enum SortOrder {
  desc = 'desc',
  asc = 'asc',
}

export enum SearchScope {
  RECORDINGS = 'RECORDINGS',
  PRIVATE_RECORDINGS = 'PRIVATE_RECORDINGS',
}

export enum SearchSortScope {
  title = 'title',
  relevance = 'relevance',
  creation_time = 'creation_time',
}

export interface SearchClause {
  type: string;
  queries: string[];
}
export interface PrivateAssetsRequest {
  limit: number;
  offset: number;
  sort: string;
  sortOrder: SortOrder;
  scope: SearchScope;
  clauses?: SearchClause[];
  divisions?: string[];
}

export interface FetchPrivateAssetsResponse {
  songs: ExtendedRecording[];
  page: PrivateAssetsPage;
}

type TUserDTO = IUser;

export type TPublicUploadingDTO = {
  fileName: string;
  id: number;
  owner: TUserDTO;
  recordingInfo: TRecordingInfoDTO;
  state: EUploadingState;
  uploadingErrorMessage?: EUploadingErrorMessage;
  uploadingMetadata?: Partial<TUploadingMetadataDTO>;
  s3UploadStatus?: EAssetUploadAsyncTaskProgress;
};

export type TRecordingInfoDTO = {
  albumInfo: TAlbumInfoDTO;
  assetType: EAssetType;
  audioInfo: TAudioInfoDTO;
  flacAudioInfo?: TAudioInfoDTO;
  comments: string;
  instrumental: boolean;
  isrc: string;
  iswc: string;
  languages: string | string[];
  lyrics: TLyricsDTO;
  notes: string;
  preRelease: boolean;
  recordingId: number;
  recordingType: TRecordingTypeDTO;
  searchMetadata: TSearchMetadataDTO;
  songInfo: TSongInfoDTO;
  streamOnly: boolean;
  thirdPartyOwner: boolean;
  userUpload: boolean;
  wcMaster: boolean;
  workCode: string;
  tagLanguages: string | string[];

  audioReplaceStatus?: EReplaceAudioStatus;
  canReplaceAudio?: boolean;
  bulkUpload?: boolean;
};

export type TUpdatedUploadRecordingDTO = Omit<TRecordingInfoDTO, 'recordingId'>;

type TAlbumInfoDTO = {
  albumTitle: string;
  artPath: string;
  artists: string[];
  featuredArtist: string[];
  labelName: string;
  releaseYear: number;
  trackNumber: number;
};

export type TAudioInfoDTO = {
  bitRate: number;
  fileName: string;
  format: EAudioFormat;
  msDuration: number;
  sampleRate: number;
};

export enum EAudioFormat {
  MP3 = 'mp3',
  MP4 = 'mp4',
  AAC = 'aac',
  AIFF = 'aiff',
  WAV = 'wav',
  FLAC = 'flac',
}

type TSongInfoDTO = {
  composer: string[];
  controlPercent: number;
  copyrightYear: number;
  publisher: string;
  songTitle: string;
};

export type TSearchMetadataDTO = {
  bpm: number;
  genres: TGenreDTO[];
  mood: TMoodDTO;
  themes: TThemeDTO[];
  versions: TVersionDTO[];
  vocalMix: TVocalMixDTO;
  tagVocalMix?: string;
  tagGenres?: string[];
  tagLanguages?: string[];
};

export type TGenreDTO = {
  code: string;
  id: number;
  primary?: boolean;
};

export type TMoodDTO = {
  code: string;
  id: number;
};

export type TThemeDTO = {
  code: string;
  id: number;
};

export type TVersionDTO = {
  code: string;
  id: number;
};

export type TVocalMixDTO = {
  code: string;
  id: number;
};

export type TLanguageDTO = {
  code: string;
  id: number;
};

export type TRecordingTypeDTO = {
  type: ERecordingType;
  demoStatus: EDemoStatus | null;
};

export type TDefaultTypeDTO = {
  code: string;
  id: any;
  primary?: boolean;
};

export enum EDemoStatus {
  AVAIlABLE = 'AVAIlABLE',
  ON_HOLD = 'ON_HOLD',
  SCHEDULED_FOR_CUT = 'SCHEDULED_FOR_CUT',
}

export enum ERecordingType {
  RECORDING = 'RECORDING',
  DEMO = 'DEMO',
}

export type TLyricsDTO = {
  lyrics: string;
  lyricsWork: string;
};

export type TUploadingMetadataDTO = {
  albumTitle: string;
  artist: string;
  bitRate: number;
  bpm: number;
  comments: string;
  composer: string;
  copyrightYear: number;
  duration: number;
  genre: string;
  iconUrl: string;
  id: number;
  label: string;
  lyrics: string;
  mood: TMoodDTO;
  publisher: string;
  releaseYear: number;
  sampleRate: number;
  songTitle: string;
  trackNumber: number;
};

enum EUploadingState {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETE = 'COMPLETE',
}

export enum EUploadingErrorMessage {
  FILE_ALREADY_EXISTS = 'FILE_ALREADY_EXISTS',
  CANNOT_READ_FILE = 'CANNOT_READ_FILE',
  CANNOT_READ_ID3 = 'CANNOT_READ_ID3',
}

export enum EAssetType {
  MASTER_ASSET = 'MASTER_ASSET',
  PUBLISHER_ASSET_WW = 'PUBLISHER_ASSET_WW',
  PUBLISHER_ASSET_YY = 'PUBLISHER_ASSET_YY',
}

export enum EAssetUploadAsyncTaskProgress {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  FAILED = 'FAILED',
  COMPLETED = 'COMPLETED',
  AUDIO_VALID = 'AUDIO_VALID',
  AUDIO_INVALID = 'AUDIO_INVALID',
}

export enum EReplaceAudioStatus {
  VALIDATING = 'VALIDATING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export type TReplaceAudioReferenceType = 'PROJECT' | 'PLAYLIST';

export type TReplaceAudioInUseReference = {
  id: number;
  name?: string;
  title?: string;
  type: TReplaceAudioReferenceType;
  url?: string;
  owner?: string;
  songCount?: number;
  updatedAt?: string;
};

export type TReplaceAudioCheckResponse =
  | TReplaceAudioInUseReference[]
  | {
      recordingId?: number;
      references: TReplaceAudioInUseReference[];
    };

export type TReplaceAudioHandshakeResponse = {
  recordingId: number;
  audioReplaceStatus: EReplaceAudioStatus;
  updatedAt: string;
  errorMessage?: string;
};

export type TReplaceAudioValidationResponse = TRecordingInfoDTO & {
  audioReplaceStatus?: EReplaceAudioStatus;
  canReplaceAudio: boolean;
  updatedAt?: string;
  errorMessage?: string;
};

export type TReplaceAudioErrorResponse = {
  code: string;
  message: string;
  references?: TReplaceAudioInUseReference[];
};

export type TUploadedCoverArtDTO = {
  artPath: string;
};

export type TAssetWork = {
  songCode: string;
  title: string;
  artists: string[];
  writers: string[];
  copyrightYear: string;
  iswc: string;
  id: number;
  controlPercent?: string | number;
};

export type TAssetWithMatchedWork = {
  recordingInfo: TRecordingInfoDTO;
  workInfo: TAssetWork;
};

export type TOptions<T> = {
  options$: Observable<T[]>;
  loading$: Observable<boolean>;
};

// GLSS-4041 — Delete-recording shared types. Kept generic so the in-use
// payload can also serve future "Replace audio" work (GLSS-3172).

export enum EInUseProjectType {
  PROJECT = 'PROJECT',
  PLAYLIST = 'PLAYLIST',
}

export type TInUseProject = {
  id: number;
  title: string;
  type?: EInUseProjectType;
  url?: string;
};

export type TDeletedBy = {
  id: number;
  name: string;
};

export type TRecordingDeletedDTO = {
  id: number;
  isDeleted: true;
  deletedAt: string;
  deletedBy?: TDeletedBy;
};

export enum EDeleteRecordingErrorReason {
  FORBIDDEN_SOURCE = 'FORBIDDEN_SOURCE',
  RECORDING_IN_USE = 'RECORDING_IN_USE',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Result of `RecordingsApiService.deleteRecording()`. Single shape (not a
 * true discriminated union) because the repo's tsconfig has
 * `strictNullChecks` disabled. Branch on `result.ok`: `deleted` is set when
 * true, `reason` is set when false.
 */
export type TDeleteRecordingResult = {
  ok: boolean;
  deleted?: TRecordingDeletedDTO;
  reason?: EDeleteRecordingErrorReason;
  projects?: TInUseProject[];
  message?: string;
};

/**
 * GLSS-4041 — Failure mode that the per-row delete popover surfaces after
 * the API responds with a non-success result. Set by the parent
 * (`BulkJobsSongsComponent`) and passed down as an Input to the row's
 * `DeleteRecordingButtonComponent`, which re-opens its popover when this
 * value transitions from `null` to a fresh object reference.
 */
export type TDeleteRecordingFailureState = {
  mode: 'in-use' | 'forbidden' | 'error';
  projects?: TInUseProject[];
  errorMessage?: string;
};
